import { Eye, EyeOff } from "lucide-react";
import { forwardRef, useId, useState, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from "react";

const control =
  "focus-ring w-full rounded-xl border border-white/70 bg-white/60 px-3 text-sm text-default shadow-[inset_0_1px_2px_rgb(15_23_42/0.06)] backdrop-blur placeholder:text-subtle transition focus:bg-white/90 disabled:opacity-60 aria-[invalid=true]:border-rose-400";

type FieldShell = { label: string; hint?: ReactNode; error?: string | null; hideLabel?: boolean };

function Shell({ id, label, hint, error, hideLabel, children }: FieldShell & { id: string; children: ReactNode }) {
  return (
    <div className="space-y-1">
      <label htmlFor={id} className={hideLabel ? "sr-only" : "block text-sm font-medium text-default"}>
        {label}
      </label>
      {children}
      {hint && !error && <p id={`${id}-hint`} className="text-xs text-subtle">{hint}</p>}
      {error && <p id={`${id}-error`} role="alert" className="text-xs text-red-600">{error}</p>}
    </div>
  );
}

export const TextField = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & FieldShell>(function TextField(
  { label, hint, error, hideLabel, id, className = "", ...rest },
  ref,
) {
  const auto = useId();
  const fid = id ?? auto;
  return (
    <Shell id={fid} label={label} hint={hint} error={error} hideLabel={hideLabel}>
      <input
        ref={ref}
        id={fid}
        aria-invalid={!!error || undefined}
        aria-describedby={error ? `${fid}-error` : hint ? `${fid}-hint` : undefined}
        className={`${control} h-10 ${className}`}
        {...rest}
      />
    </Shell>
  );
});

/**
 * Password input with a show/hide toggle. The toggle is a real button (keyboard accessible,
 * announces its state); the value reverts to hidden whenever the field is remounted.
 */
export const PasswordField = forwardRef<HTMLInputElement, Omit<InputHTMLAttributes<HTMLInputElement>, "type"> & FieldShell>(function PasswordField(
  { label, hint, error, hideLabel, id, className = "", ...rest },
  ref,
) {
  const auto = useId();
  const fid = id ?? auto;
  const [visible, setVisible] = useState(false);
  return (
    <Shell id={fid} label={label} hint={hint} error={error} hideLabel={hideLabel}>
      <div className="relative">
        <input
          ref={ref}
          id={fid}
          type={visible ? "text" : "password"}
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          aria-invalid={!!error || undefined}
          aria-describedby={error ? `${fid}-error` : hint ? `${fid}-hint` : undefined}
          className={`${control} h-10 pr-11 ${className}`}
          {...rest}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? `Hide ${label.toLowerCase()}` : `Show ${label.toLowerCase()}`}
          aria-pressed={visible}
          aria-controls={fid}
          className="focus-ring absolute inset-y-0 right-1 my-auto grid h-8 w-8 place-items-center rounded-lg text-subtle transition hover:bg-white/80 hover:text-default"
        >
          {visible ? <EyeOff className="h-4 w-4" aria-hidden="true" /> : <Eye className="h-4 w-4" aria-hidden="true" />}
        </button>
      </div>
    </Shell>
  );
});

export function SelectField({ label, hint, error, hideLabel, id, children, className = "", ...rest }: SelectHTMLAttributes<HTMLSelectElement> & FieldShell) {
  const auto = useId();
  const fid = id ?? auto;
  return (
    <Shell id={fid} label={label} hint={hint} error={error} hideLabel={hideLabel}>
      <select id={fid} aria-invalid={!!error || undefined} className={`${control} h-10 ${className}`} {...rest}>
        {children}
      </select>
    </Shell>
  );
}

export function TextAreaField({ label, hint, error, hideLabel, id, className = "", ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement> & FieldShell) {
  const auto = useId();
  const fid = id ?? auto;
  return (
    <Shell id={fid} label={label} hint={hint} error={error} hideLabel={hideLabel}>
      <textarea id={fid} aria-invalid={!!error || undefined} className={`${control} py-2 ${className}`} {...rest} />
    </Shell>
  );
}

export function Checkbox({ label, description, ...rest }: InputHTMLAttributes<HTMLInputElement> & { label: string; description?: string }) {
  const id = useId();
  return (
    <div className="flex items-start gap-2">
      <input id={id} type="checkbox" className="focus-ring mt-0.5 h-4 w-4 rounded border-default accent-accent-600" {...rest} />
      <label htmlFor={id} className="text-sm">
        <span className="font-medium text-default">{label}</span>
        {description && <span className="block text-xs text-subtle">{description}</span>}
      </label>
    </div>
  );
}
